import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { 
  ArrowLeftIcon, PencilIcon, KeyIcon, 
  ShieldCheckIcon, UserGroupIcon 
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const UserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { get, put, loading } = useApi();
  const [user, setUser] = useState(null);
  const [userRoles, setUserRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [editingRoles, setEditingRoles] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const isAdmin = currentUser?.roles?.includes('ADMIN') || false;
  const isOwnProfile = currentUser?.username === user?.username;

  useEffect(() => {
    fetchUser();
    fetchAvailableRoles();
  }, [id]);

  useEffect(() => {
    if (user) {
      fetchUserRoles();
    }
  }, [user]);

  const fetchUser = async () => {
    const { data } = await get(`/api/users/${id}`);
    if (data) setUser(data);
  };

  const fetchUserRoles = async () => {
    setLoadingRoles(true);
    const { data } = await get(`/api/users/${id}/roles`);
    if (data) {
      setUserRoles(data);
      setSelectedRoles(data.map(r => r.roleId));
    }
    setLoadingRoles(false);
  };

  const fetchAvailableRoles = async () => {
    const { data } = await get('/api/roles');
    if (data) setAvailableRoles(data);
  };

  const handleRoleToggle = (roleId) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handleSaveRoles = async () => {
    setLoadingRoles(true);
    const { error } = await put(`/api/users/${id}/roles`, { roleIds: selectedRoles });
    if (!error) {
      toast.success('Roles updated successfully');
      setEditingRoles(false);
      fetchUserRoles();
    } else {
      toast.error(error || 'Failed to update roles');
    }
    setLoadingRoles(false);
  };

  if (loading) return <LoadingSpinner />;
  if (!user) return <div className="p-6 text-gray-900 dark:text-white">User not found</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/users')}
            className="mr-4 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">User Details</h1>
        </div>
        
        <div className="flex space-x-2">
          {isAdmin && (
            <button
              onClick={() => navigate(`/users/${id}/reset-password`)}
              className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 flex items-center space-x-2"
            >
              <KeyIcon className="h-5 w-5" />
              <span>Reset Password</span>
            </button>
          )}
          {isOwnProfile && (
            <button
              onClick={() => navigate('/change-password')}
              className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 flex items-center space-x-2"
            >
              <KeyIcon className="h-5 w-5" />
              <span>Change Password</span>
            </button>
          )}
          {(isAdmin || isOwnProfile) && (
            <button
              onClick={() => navigate(`/users/edit/${id}`)}
              className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center space-x-2"
            >
              <PencilIcon className="h-5 w-5" />
              <span>Edit</span>
            </button>
          )}
        </div>
      </div>

      {/* User Info Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
          <UserGroupIcon className="h-5 w-5 mr-2" />
          User Information
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">User ID</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">{user.userId}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Username</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">{user.username}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Full Name</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">{user.fullName}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Email</label>
            <p className="text-lg font-medium text-gray-900 dark:text-white">{user.email}</p>
          </div>
          <div>
            <label className="text-sm text-gray-500 dark:text-gray-400">Status</label>
            <p className="text-lg font-medium">
              <span className={`px-2 py-1 rounded-full text-xs ${
                user.isActive ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
              }`}>
                {user.isActive ? 'Active' : 'Inactive'}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Roles & Permissions Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
            <ShieldCheckIcon className="h-5 w-5 mr-2" />
            Roles & Permissions
          </h2>
          {isAdmin && !editingRoles && (
            <button
              onClick={() => setEditingRoles(true)}
              className="text-indigo-600 hover:text-indigo-800 text-sm"
            >
              Edit Roles
            </button>
          )}
        </div>
        
        {loadingRoles ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          </div>
        ) : editingRoles ? (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
              {availableRoles.map(role => (
                <label key={role.roleId} className="flex items-center space-x-2 p-2 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                  <input
                    type="checkbox"
                    checked={selectedRoles.includes(role.roleId)}
                    onChange={() => handleRoleToggle(role.roleId)}
                    className="h-4 w-4 text-indigo-600 rounded"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{role.roleName}</span>
                    {role.roleDescription && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{role.roleDescription}</p>
                    )}
                  </div>
                </label>
              ))}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setEditingRoles(false)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRoles}
                className="px-3 py-1 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Save Roles
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {userRoles.length > 0 ? (
              userRoles.map(role => (
                <div key={role.roleId} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <h3 className="font-medium text-gray-900 dark:text-white">{role.roleName}</h3>
                  {role.roleDescription && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{role.roleDescription}</p>
                  )}
                  
                  {/* Permissions */}
                  {role.permissions && role.permissions.length > 0 && (
                    <div className="mt-3">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Permissions:</h4>
                      <div className="flex flex-wrap gap-2">
                        {role.permissions.map(perm => (
                          <span
                            key={perm.permId}
                            className="px-2 py-1 bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 rounded-full text-xs"
                          >
                            {perm.permName}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4">No roles assigned</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetails;